import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BellSimple, Megaphone, Sparkle, Warning, ListChecks, UserCirclePlus, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { loadAnnouncements } from "../lib/announcementsStore";
import { timeAgo } from "../lib/timeago";

const SEEN = "aa_seen_announcements"; // { announcementId: version } — what this device has already looked at
const readSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN) || "{}") || {}; } catch { return {}; } };
const writeSeen = (v) => { try { localStorage.setItem(SEEN, JSON.stringify(v)); } catch { /* private mode */ } };

const ICONS = {
  request: { Icon: ListChecks, tint: "#7FE7A5" },
  credit: { Icon: UserCirclePlus, tint: "#FF8090" },
  info: { Icon: Megaphone, tint: "#9DB8FF" },
  new: { Icon: Sparkle, tint: "#FF8090" },
  warning: { Icon: Warning, tint: "#F5B544" },
};

/**
 * The little bell next to the profile picture: what happened to your requests, scenepacks
 * credited to you, and the site announcements — with a red count for anything you haven't seen.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState({ items: [], unread: 0 });
  const [annos, setAnnos] = useState([]);
  const [seen, setSeen] = useState(readSeen);
  const [fresh, setFresh] = useState(() => new Set()); // what was unread when the panel opened — stays highlighted while it's open
  const [pos, setPos] = useState({ top: 76 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const lastFetch = useRef(0);

  const refresh = useCallback(async (force = false) => {
    lastFetch.current = Date.now();
    const [n, a] = await Promise.all([
      api.get("/notifications").then((r) => r.data).catch(() => null),
      loadAnnouncements(force),
    ]);
    if (n) setMine(n);
    setAnnos(a);
    return { n, a };
  }, []);

  useEffect(() => {
    refresh();
    // coming back to the tab after a while: look again (no timers polling in the background)
    const onVis = () => { if (document.visibilityState === "visible" && Date.now() - lastFetch.current > 120_000) refresh(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  const unseenAnnos = annos.filter((a) => seen[a.id] !== a.version);
  const count = mine.unread + unseenAnnos.length;

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: Math.round(r.bottom + 10) });
    setOpen(true);
    const { n, a } = await refresh(true);
    const f = new Set([...(n?.items || []).filter((i) => !i.read).map((i) => i.id), ...(a || []).filter((x) => seen[x.id] !== x.version).map((x) => `a:${x.id}`)]);
    setFresh(f);
    // opening it counts as reading it
    if (n?.unread) {
      api.post("/notifications/read", {}).catch(() => {});
      setMine((m) => ({ items: m.items.map((i) => ({ ...i, read: true })), unread: 0 }));
    }
    const next = { ...seen };
    (a || []).forEach((x) => { next[x.id] = x.version; });
    setSeen(next);
    writeSeen(next);
  };

  // close: outside click, Escape, or moving to another page
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away, { passive: true });
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("touchstart", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const items = [
    ...mine.items.map((i) => ({ key: i.id, kind: i.kind, title: i.title, body: i.body, link: i.link_url, at: i.created_at, fresh: fresh.has(i.id), tag: "" })),
    ...annos.map((a) => ({ key: `a:${a.id}`, kind: a.kind, title: a.title || (a.message.length > 60 ? "Announcement" : a.message), body: a.title ? a.message : a.message.length > 60 ? a.message : "", link: a.link_url, at: a.version, fresh: fresh.has(`a:${a.id}`), tag: "ALPHA" })),
  ].sort((x, y) => (y.at || "").localeCompare(x.at || ""));

  const go = (link) => {
    setOpen(false);
    if (!link) return;
    if (link.startsWith("/")) navigate(link);
    else window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={count ? `Notifications, ${count} new` : "Notifications"}
        aria-expanded={open}
        data-testid="notification-bell"
        className="relative w-10 h-10 flex items-center justify-center rounded-full text-white/75 hover:text-white hover:bg-white/5 active:bg-white/10 transition"
      >
        <BellSimple size={21} weight={open || count ? "fill" : "regular"} className={count && !open ? "bell-wiggle" : ""} />
        {count > 0 && (
          <span
            className="absolute top-1 right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#E0203F] text-white text-[10px] font-bold font-mono leading-[17px] text-center ring-2 ring-[#121212]"
            data-testid="notification-count"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
              exit={{ opacity: 0, y: -6, transition: { duration: 0.12 } }}
              style={{ top: pos.top }}
              className="fixed z-[55] left-3 right-3 sm:left-auto sm:right-6 sm:w-[390px] rounded-2xl border border-white/10 bg-[#161616] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.95)] overflow-hidden"
              role="dialog"
              aria-label="Notifications"
              data-testid="notification-panel"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="font-display font-bold text-sm">Notifications</div>
                {items.length > 0 && <div className="text-[10px] font-mono uppercase tracking-wider text-white/35">{items.length} recent</div>}
              </div>

              <div className="max-h-[min(62vh,460px)] overflow-y-auto overscroll-contain">
                {items.length === 0 ? (
                  <div className="px-6 py-12 text-center" data-testid="notification-empty">
                    <div className="mx-auto w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3"><BellSimple size={22} className="text-white/40" /></div>
                    <div className="font-display font-bold text-sm mb-1">You're all caught up</div>
                    <p className="text-xs text-white/45 leading-relaxed">News from ALPHA, updates on the scenepacks you request, and scenepacks credited to you will show up here.</p>
                  </div>
                ) : (
                  items.map((n) => {
                    const { Icon, tint } = ICONS[n.kind] || ICONS.info;
                    return (
                      <button
                        key={n.key}
                        type="button"
                        onClick={() => go(n.link)}
                        data-testid={`notification-${n.key}`}
                        className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-b border-white/5 last:border-0 transition hover:bg-white/[0.04] ${n.fresh ? "bg-[#880015]/[0.08]" : ""} ${n.link ? "cursor-pointer" : "cursor-default"}`}
                      >
                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${tint}1f`, color: tint }}>
                          <Icon size={15} weight="fill" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-2">
                            <span className="font-display font-bold text-[13px] leading-snug break-words">{n.title}</span>
                            {n.fresh && <span className="w-2 h-2 rounded-full bg-[#E0203F] flex-shrink-0 mt-1.5" aria-label="New" />}
                          </span>
                          {n.body && <span className="block text-xs text-white/55 leading-relaxed mt-0.5 break-words whitespace-pre-line">{n.body}</span>}
                          <span className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-white/35">
                            {n.tag && <span className="px-1.5 py-px rounded bg-white/8 text-white/55 tracking-wider">{n.tag}</span>}
                            {timeAgo(n.at)}
                            {n.link && <ArrowRight size={10} className="ml-auto text-white/30" />}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <Link
                to="/requests"
                onClick={() => setOpen(false)}
                data-testid="notification-requests-link"
                className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-[11px] font-display font-bold uppercase tracking-wider text-[#FF8090] hover:bg-white/[0.04] transition"
              >
                <span>Request a scenepack</span>
                <ArrowRight size={12} weight="bold" />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
